import { Fragment, useState } from 'react';
import { api, type OrderEvent } from '../api';
import { label, LoginAsCamp, money, STOREFRONT_HOST, useDebounced, useLoad } from '../lib';
import { OrderDetailView, plainComment } from './OrderDetail';

// The order lifecycle board. Statuses mirror the commerce state machine; `allowedNext` is the set of
// legal transitions the machine returns per order — advancing is an ops mutation (BFF gates admin/ops).
//
// And the lookup a support call starts with (go/no-go #22): find one order by its PO number, by the
// camp, or by the vendor — each box narrows, together they combine, and "All" searches every status.
//
// Opening an order shows the Adobe order screen's tools (OrderDetail.tsx). From the queue itself, as on
// Adobe's grid: the order tags column and filter, and a status change for every ticked order at once.
const STATUSES = ['pending', 'acknowledged', 'invoice_received', 'complete', 'canceled'];

export function OrderQueuePanel({ canEdit }: { canEdit: boolean }) {
  const [status, setStatus] = useState('pending');
  const [find, setFind] = useState({ po: '', camp: '', vendor: '' });
  const [tagId, setTagId] = useState<number | null>(null);
  const f = useDebounced(find, 300);
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [detail, setDetail] = useState<number | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [bulkTo, setBulkTo] = useState('');
  const [newOrder, setNewOrder] = useState(false);
  const { data, err, reload } = useLoad(() => api.commerceOrders({ status, ...f, tagId }), [status, f.po, f.camp, f.vendor, tagId]);
  const { data: tags } = useLoad(() => api.commerceOrderTags(), []);
  const searching = !!(f.po.trim() || f.camp.trim() || f.vendor.trim() || tagId);

  async function advance(id: number, to: string) {
    setBusy(id);
    setMsg(null);
    try {
      await api.commerceSetOrderStatus(id, to);
      setMsg(`#${id} → ${label(to)}`);
      reload();
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function bulk() {
    if (!bulkTo || picked.size === 0) return;
    setBusy(-1);
    setMsg(null);
    try {
      const r = await api.commerceBulkOrderStatus([...picked], bulkTo);
      const refused = r.results.filter((x) => !x.ok).map((x) => `#${x.id}: ${x.error}`);
      setMsg(`${r.failed ? '⚠ ' : ''}${r.changed} order${r.changed === 1 ? '' : 's'} → ${label(bulkTo)}${refused.length ? ` · ${r.failed} refused — ${refused.join('; ')}` : ''}`);
      setPicked(new Set());
      setBulkTo('');
      reload();
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  if (detail != null) return <OrderDetailView id={detail} canEdit={canEdit} onBack={() => { setDetail(null); reload(); }} />;

  const rows = data ?? [];
  const toggle = (id: number) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // The statuses every ticked order may move to — the intersection of their allowed transitions.
  const common = rows.filter((r) => picked.has(r.id)).reduce<string[] | null>((acc, r) => (acc == null ? r.allowedNext : acc.filter((s) => r.allowedNext.includes(s))), null) ?? [];

  return (
    <div className="stack">
      <div className="od-card-head">
        <h1>Order Queue</h1>
        {canEdit && <button className="btn-sm" onClick={() => setNewOrder(!newOrder)}>{newOrder ? 'Close' : 'New order for a camp'}</button>}
      </div>
      <p className="sub">Find an order by PO number, camp, vendor or tag, open it to edit its items, comment and tag it — every change is checked against the vendor's rules and recorded</p>
      {newOrder && <NewOrderForCamp />}
      <div className="toolbar">
        <input className="search" aria-label="PO number" placeholder="PO # or order #…" value={find.po} onChange={(e) => setFind({ ...find, po: e.target.value })} />
        <input className="search" aria-label="Camp" placeholder="Camp…" value={find.camp} onChange={(e) => setFind({ ...find, camp: e.target.value })} />
        <input className="search" aria-label="Vendor" placeholder="Vendor…" value={find.vendor} onChange={(e) => setFind({ ...find, vendor: e.target.value })} />
        <select className="sel" aria-label="Order tag" value={tagId ?? ''} onChange={(e) => setTagId(e.target.value ? Number(e.target.value) : null)}>
          <option value="">Any tag</option>
          {(tags ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        {searching && <button className="btn-sm ghost" onClick={() => { setFind({ po: '', camp: '', vendor: '' }); setTagId(null); }}>Clear</button>}
      </div>
      <div className="toolbar">
        <button className={'chip' + (status === '' ? ' on' : '')} onClick={() => setStatus('')}>All</button>
        {STATUSES.map((s) => (
          <button key={s} className={'chip' + (status === s ? ' on' : '')} onClick={() => setStatus(s)}>{label(s)}</button>
        ))}
      </div>
      {canEdit && picked.size > 0 && (
        <div className="toolbar od-bulk">
          <strong>{picked.size} selected</strong>
          <select className="sel" aria-label="Change selected orders to" value={bulkTo} onChange={(e) => setBulkTo(e.target.value)}>
            <option value="">{common.length ? 'Change status to…' : 'No status fits every selected order'}</option>
            {common.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <button className="btn-sm" disabled={!bulkTo || busy === -1} onClick={bulk}>{busy === -1 ? 'Changing…' : 'Apply'}</button>
          <button className="btn-sm ghost" onClick={() => setPicked(new Set())}>Clear selection</button>
        </div>
      )}
      {err && <p className="msg bad">⚠ {err}</p>}
      {msg && <p className={`msg ${msg.startsWith('⚠') ? 'bad' : 'good'}`}>{msg}</p>}
      <section className="card">
        <div className="od-scroll">
          <table>
            <thead><tr>
              {canEdit && <th><input type="checkbox" aria-label="Select every order shown" checked={rows.length > 0 && rows.every((r) => picked.has(r.id))}
                onChange={(e) => setPicked(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>}
              <th>PO #</th><th>Tags</th><th>Camp</th><th>Vendor</th><th>Placed</th><th>Status</th><th className="num">Total</th><th>Advance to</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <tr>
                    {canEdit && <td><input type="checkbox" aria-label={`Select ${r.po_number ?? r.increment_id}`} checked={picked.has(r.id)} onChange={() => toggle(r.id)} /></td>}
                    <td className="mono">
                      <button className="link edit" onClick={() => setDetail(r.id)} title="Open the order">{r.po_number ?? r.increment_id}</button>
                      {r.source === 'checkout' && <span className="pill" style={{ marginLeft: 4 }}>new</span>}
                      {r.po_number && r.increment_id && r.po_number !== r.increment_id && <div className="muted">order {r.increment_id}</div>}
                    </td>
                    <td>{(r.tags ?? []).map((t) => <span key={t.id} className="pill tag">{t.name}</span>)}{r.notifications_muted && <span className="pill" title="Notifications muted">muted</span>}</td>
                    <td>{r.camp ?? '—'}</td>
                    <td>{r.vendor ?? '—'}</td>
                    <td className="mono muted">{r.created_at?.slice(0, 10) ?? '—'}</td>
                    <td><span className="pill type">{label(r.status)}</span></td>
                    <td className="num">{money(r.grand_total)}</td>
                    <td>
                      {r.allowedNext.length === 0 ? (
                        <span className="muted">— terminal —</span>
                      ) : (
                        <select className="sel" disabled={busy === r.id || !canEdit} defaultValue="" onChange={(e) => e.target.value && advance(r.id, e.target.value)}>
                          <option value="" disabled>{busy === r.id ? 'updating…' : 'choose…'}</option>
                          {r.allowedNext.map((n) => <option key={n} value={n}>{label(n)}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="actions">
                      <button className="btn-sm" onClick={() => setDetail(r.id)}>Open</button>
                      <button className="btn-sm ghost" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Hide' : 'History'}</button>
                    </td>
                  </tr>
                  {open === r.id && <tr><td colSpan={canEdit ? 10 : 9}><OrderHistory id={r.id} /></td></tr>}
                </Fragment>
              ))}
              {data && data.length === 0 && (
                <tr><td colSpan={canEdit ? 10 : 9} className="loading">{searching ? `No ${status ? `${label(status)} ` : ''}order matches.` : `No ${label(status)} orders.`}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {data && data.length === 100 && <p className="muted" style={{ marginTop: 8 }}>Showing the newest 100 — narrow the search to find an older order.</p>}
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}

// "Create New Order" on Adobe's grid. Here it is the storefront's own checkout, opened acting for the
// camp under the staff member's own sign-in: the cart, the catalog, the delivery-date and minimum rules
// and the act-as audit are the buyer's path exactly, and the PO is recorded as the camp's with the staff
// member on its audit row. The admin center's service key has no person behind it, so it never places
// an order itself.
function NewOrderForCamp() {
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const { data, err } = useLoad(() => (dq.trim().length >= 2 ? api.commerceOrgs(dq.trim()) : Promise.resolve([])), [dq]);
  return (
    <section className="card">
      <h2>New order for a camp</h2>
      <p className="muted small">Pick the camp, then place the order in the storefront acting for it ({STOREFRONT_HOST}, signed in as yourself). It goes through the camp's cart and checkout, with the vendor's delivery and minimum rules, and is recorded as placed by you for the camp.</p>
      <input className="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Camp name…" aria-label="Camp to order for" />
      {err && <p className="msg bad">⚠ {err}</p>}
      {dq.trim().length >= 2 && data && (
        <table>
          <tbody>
            {data.length === 0 && <tr><td className="muted">No active camp matches.</td></tr>}
            {data.slice(0, 10).map((c) => (
              <tr key={c.id}><td>{c.name}</td><td className="num"><LoginAsCamp orgId={c.id} name={c.name} /></td></tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// One order's history, oldest first — including what the vendor's acknowledgement said line by line.
function OrderHistory({ id }: { id: number }) {
  const { data, err } = useLoad(() => api.commerceOrderEvents(id), [id]);
  if (err) return <p className="msg bad">⚠ {err}</p>;
  if (!data) return <p className="loading">Loading history…</p>;
  if (data.events.length === 0) return <p className="muted">No history recorded for this order.</p>;
  return (
    <ol className="order-history">
      {data.events.map((e: OrderEvent, i: number) => (
        <li key={i}>
          <span className="mono muted">{e.created_at?.slice(0, 16) ?? '—'}</span> <span className="pill type">{label(e.status)}</span>
          {e.comment && <div className="order-history-comment">{plainComment(e.comment)}</div>}
        </li>
      ))}
    </ol>
  );
}
